/**
 * Automatic sign-out after inactivity (security, release 1.5.0).
 *
 * One idle clock for all open tabs: the time of the last user activity lives in
 * `localStorage[LAST_ACTIVITY_KEY]` (written at most every
 * ACTIVITY_WRITE_INTERVAL_MS, with a trailing write so the last interaction is
 * never lost), and every tab reads it before it warns or signs out. Activity is
 * only what the user does (pointer, keyboard, wheel/scroll, touch, returning to
 * the tab) — background requests, polling and token refreshes never touch it.
 *
 * - IdleSessionGuard (components/auth) listens for activity, warns
 *   IDLE_SESSION_POLICY.warningMs before the timeout (WCAG 2.2.1) and calls
 *   markIdleSignedOut() when the timeout is reached on an authenticated page.
 * - AuthProvider subscribes to that flag: it revokes the refresh token, clears
 *   the session and the cached data, and tells the other tabs (IDLE_SIGNOUT_KEY).
 *   On start it never restores a session whose last activity is too old.
 * - sessionRefresh refuses to renew an idle session (laptop sleep, throttled
 *   timers in a hidden tab), so the stale check does not rely on setTimeout.
 * - ProtectedRoute sends the user to /login?redirect=… and LoginPage shows the
 *   notice; the flag is cleared by the next sign-in.
 */

const MINUTE_MS = 60_000;

export interface IdleSessionPolicy {
  /** Sign out after this long without user activity. */
  timeoutMs: number;
  /** Show the warning dialog this long before the sign-out. */
  warningMs: number;
}

/**
 * The single place that defines the idle timeout: 60 minutes, warning 2 minutes
 * before. E2E runs may shorten it with VITE_IDLE_TIMEOUT_MS (the warning then
 * takes at most half of the timeout).
 */
export function resolveIdleSessionPolicy(override?: unknown): IdleSessionPolicy {
  const defaults: IdleSessionPolicy = { timeoutMs: 60 * MINUTE_MS, warningMs: 2 * MINUTE_MS };
  const parsed = typeof override === 'string' || typeof override === 'number' ? Number(override) : NaN;
  if (!Number.isFinite(parsed) || parsed <= 0) return defaults;
  return { timeoutMs: parsed, warningMs: Math.min(defaults.warningMs, Math.floor(parsed / 2)) };
}

export const IDLE_SESSION_POLICY: IdleSessionPolicy = resolveIdleSessionPolicy(
  import.meta.env.VITE_IDLE_TIMEOUT_MS,
);

/** Minutes for the user-facing copy ("in 2 minutes", "after 60 minutes"). */
export function policyMinutes(ms: number): number {
  return Math.max(1, Math.round(ms / MINUTE_MS));
}

/** The shared idle clock (epoch ms of the last user activity). */
export const LAST_ACTIVITY_KEY = 'reefguide_last_activity';
/** Written by the tab that signed out for inactivity, so the other tabs show the same notice. */
export const IDLE_SIGNOUT_KEY = 'reefguide_idle_signout';
/** Activity is written to localStorage at most this often. */
export const ACTIVITY_WRITE_INTERVAL_MS = 30_000;

// ── Shared idle clock ───────────────────────────────────────────────────────

let lastLocalActivity = 0;
let lastWriteAt = 0;
let trailingWrite: ReturnType<typeof setTimeout> | null = null;

/** The stored last activity, or null when there is none / it is malformed. Never throws. */
export function readLastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeLastActivity(): void {
  if (trailingWrite !== null) {
    clearTimeout(trailingWrite);
    trailingWrite = null;
  }
  lastWriteAt = Date.now();
  try {
    // Never move the shared clock backwards (another tab may have written later).
    const stored = readLastActivity() ?? 0;
    if (lastLocalActivity > stored) localStorage.setItem(LAST_ACTIVITY_KEY, String(lastLocalActivity));
  } catch {
    // storage unavailable — this tab keeps its own clock
  }
}

/**
 * Records a user activity. Throttled: written at once when the last write is
 * older than ACTIVITY_WRITE_INTERVAL_MS, else by a trailing write. `force`
 * writes immediately (sign-in, "Stay signed in").
 */
export function recordActivity(options: { force?: boolean } = {}): void {
  const now = Date.now();
  lastLocalActivity = Math.max(lastLocalActivity, now);
  const sinceWrite = now - lastWriteAt;
  if (options.force || sinceWrite >= ACTIVITY_WRITE_INTERVAL_MS || sinceWrite < 0) {
    writeLastActivity();
    return;
  }
  if (trailingWrite === null) {
    trailingWrite = setTimeout(writeLastActivity, ACTIVITY_WRITE_INTERVAL_MS - sinceWrite);
  }
}

/** Forgets this tab's activity (sign-out; tests). */
export function resetActivityTracking(): void {
  if (trailingWrite !== null) clearTimeout(trailingWrite);
  trailingWrite = null;
  lastLocalActivity = 0;
  lastWriteAt = 0;
}

/** The latest activity of any tab, or null when none is known. */
export function lastActivityAt(): number | null {
  const latest = Math.max(lastLocalActivity, readLastActivity() ?? 0);
  return latest > 0 ? latest : null;
}

/**
 * True when the last known activity is at least the idle timeout ago. A
 * session without any recorded activity (first start after this feature
 * shipped, E2E fixtures) is not idle — the clock starts now.
 */
export function isIdleExpired(now: number = Date.now()): boolean {
  const last = lastActivityAt();
  return last !== null && now - last >= IDLE_SESSION_POLICY.timeoutMs;
}

// ── "Signed out for inactivity" flag ───────────────────────────────────────

type Listener = () => void;
let idleSignedOut = false;
const listeners = new Set<Listener>();

export function markIdleSignedOut(): void {
  if (idleSignedOut) return;
  idleSignedOut = true;
  listeners.forEach((listener) => listener());
}

export function isIdleSignedOut(): boolean {
  return idleSignedOut;
}

export function clearIdleSignedOut(): void {
  if (!idleSignedOut) return;
  idleSignedOut = false;
  listeners.forEach((listener) => listener());
}

export function subscribeIdleSignedOut(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tells the other tabs that this tab signed out for inactivity (storage event). */
export function broadcastIdleSignOut(): void {
  try {
    localStorage.setItem(IDLE_SIGNOUT_KEY, String(Date.now()));
  } catch {
    // storage unavailable — the other tabs still sign out when the tokens disappear
  }
}

// ── Authenticated pages ─────────────────────────────────────────────────────

/**
 * The warning and the timed sign-out only run while an authenticated page
 * (ProtectedRoute) is shown — never on login, register or the public pages.
 */
let protectedPages = 0;
const pageListeners = new Set<Listener>();

export function registerProtectedPage(): () => void {
  protectedPages += 1;
  pageListeners.forEach((listener) => listener());
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    protectedPages -= 1;
    pageListeners.forEach((listener) => listener());
  };
}

export function isOnProtectedPage(): boolean {
  return protectedPages > 0;
}

export function subscribeProtectedPages(listener: Listener): () => void {
  pageListeners.add(listener);
  return () => {
    pageListeners.delete(listener);
  };
}
